/**
 * filterUrl.ts
 *
 * Serializes FilterState + SortMode to/from URL search params.
 * Allows users to share filtered views as links.
 *
 * URL format example:
 *   ?now=1&days=Mon,Fri&deals=beer,cocktail&cat=rooftop&hood=OTR&sort=cheapest&q=wings
 *
 * Design rules:
 * - Keep param names short (URL real estate)
 * - Sets serialize as comma-separated strings
 * - Booleans serialize as "1" / omitted (no "false" in URL)
 * - Defaults are omitted (empty URL = default state)
 * - Invalid/unknown values are silently ignored on parse
 *
 * REMOVED: timeWindow (tw) and priceTiers (price) — no longer in UI
 * ADDED: dogFriendly (dog) — properly typed now
 * FIXED: liquor was missing from VALID_DEALS
 * FIXED: waterfront added to VALID_CATS
 */

import type { FilterState, SortMode, DayOfWeek, DealType, VenueCategory } from '../types'
import { DEFAULT_FILTERS } from '../types'

// ─────────────────────────────────────────────
// PARAM KEYS
// ─────────────────────────────────────────────

const P = {
  NOW:   'now',
  DOG:   'dog',
  DAYS:  'days',
  DEALS: 'deals',
  CATS:  'cat',
  HOODS: 'hood',
  SORT:  'sort',
  QUERY: 'q',
  CITY:  'city',
} as const

// ─────────────────────────────────────────────
// VALID VALUE SETS (for safe parsing)
// ─────────────────────────────────────────────

const VALID_DAYS  = new Set<DayOfWeek>(['Mon','Tue','Wed','Thu','Fri','Sat','Sun'])

// liquor was missing — added. All 6 deal types now valid.
const VALID_DEALS = new Set<DealType>(['beer','wine','cocktail','liquor','food','general'])

// waterfront added. patio, live_music already present.
const VALID_CATS  = new Set<VenueCategory>([
  'sports_bar','dive_bar','cocktail_bar','rooftop','patio','restaurant',
  'brewery','wine_bar','pub','lounge','date_night','live_music','late_night',
  'waterfront',
])

const VALID_SORTS = new Set<SortMode>([
  'best_match','closest','cheapest','starting_soon','most_popular','featured',
])

// ─────────────────────────────────────────────
// SERIALIZE — FilterState → URLSearchParams
// ─────────────────────────────────────────────

export function filtersToParams(
  filters: FilterState,
  sort: SortMode
): URLSearchParams {
  const p = new URLSearchParams()

  if (filters.openNow)                    p.set(P.NOW,   '1')
  if ((filters as any).dogFriendly)       p.set(P.DOG,   '1')
  if (filters.days.size > 0)              p.set(P.DAYS,  [...filters.days].join(','))
  if (filters.dealTypes.size > 0)         p.set(P.DEALS, [...filters.dealTypes].join(','))
  if (filters.categories.size > 0)        p.set(P.CATS,  [...filters.categories].join(','))
  if (filters.neighborhoods.size > 0)     p.set(P.HOODS, [...filters.neighborhoods].join(','))
  if (filters.search.trim())              p.set(P.QUERY, filters.search.trim())
  if (filters.city)                       p.set(P.CITY,  filters.city)
  if (sort !== 'featured')                p.set(P.SORT,  sort)  // omit default

  return p
}

// ─────────────────────────────────────────────
// PARSE — URLSearchParams → FilterState + SortMode
// ─────────────────────────────────────────────

export function paramsToFilters(
  params: URLSearchParams
): { filters: FilterState; sort: SortMode } {
  function parseSet<T>(key: string, valid: Set<T>): Set<T> {
    const raw = params.get(key)
    if (!raw) return new Set()
    const items = raw.split(',').filter(v => valid.has(v as T)) as T[]
    return new Set(items)
  }

  const sortRaw = params.get(P.SORT)
  // FALLBACK MUST MATCH THE SERIALIZE DEFAULT.
  // filtersToParams omits sort when it equals 'featured', so a URL with
  // any other param but no sort param means "default sort" = 'featured'.
  // Falling back to 'best_match' here silently flipped the sort on every
  // reloaded/shared filtered link — featured venues lost their paid-for
  // top placement and the Filters badge showed a phantom (1).
  const sort: SortMode = sortRaw && VALID_SORTS.has(sortRaw as SortMode)
    ? sortRaw as SortMode
    : 'featured'

  const filters: FilterState = {
    ...DEFAULT_FILTERS,
    openNow:       params.get(P.NOW) === '1',
    days:          parseSet(P.DAYS,  VALID_DAYS),
    dealTypes:     parseSet(P.DEALS, VALID_DEALS),
    categories:    parseSet(P.CATS,  VALID_CATS),
    neighborhoods: new Set<string>(),
    search:        params.get(P.QUERY) ?? '',
    city:          params.get(P.CITY) ?? null,
  } as any

  // dogFriendly — parsed from URL into filter state
  ;(filters as any).dogFriendly = params.get(P.DOG) === '1'

  // neighborhoods is an open set — re-parse without validation
  const hoodRaw = params.get(P.HOODS)
  if (hoodRaw) {
    filters.neighborhoods = new Set(hoodRaw.split(',').filter(Boolean))
  }

  return { filters, sort }
}

// ─────────────────────────────────────────────
// ACTIVE FILTER COUNT — for badge display
// Only counts filters that are actually in the UI
// ─────────────────────────────────────────────

export function countActiveFilters(filters: FilterState, sort: SortMode): number {
  return [
    filters.openNow,
    (filters as any).dogFriendly,
    filters.days.size > 0,
    filters.dealTypes.size > 0,
    filters.categories.size > 0,
    filters.neighborhoods.size > 0,
    filters.search.trim().length > 0,
    sort !== 'featured',  // 'featured' is the default sort — don't count it as active
  ].filter(Boolean).length
}

/** True if filters + sort are at their default values */
export function isDefaultState(filters: FilterState, sort: SortMode): boolean {
  return countActiveFilters(filters, sort) === 0
}
